"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { getNavigationLinks } from "@/services/api/navbar";
import { useUser } from "@/contexts/user-context";
import dynamic from "next/dynamic";
const LoginPopup = dynamic(() => import("@/components/LoginPopup"), {
  ssr: false,
  loading: () => null
});
import DesktopHeader from "@/components/header/DesktopHeader";
import MobileHeader from "@/components/header/MobileHeader";
import NavigationMenu from "@/components/header/NavigationMenu";
import MobileSidebar from "@/components/header/MobileSidebar";
import { HeaderProps } from "@/components/header/types";
import { textVariants } from "@/components/header/constants";
import { useCheckout } from "@/contexts/checkout";
import { useHeader } from "@/contexts/header-context";
const DEFAULT_ARR: unknown[] = [];
const ANNOUNCEMENT_ITEMS = [{
  text: "95% on-time delivery ",
  highlight: "₹200 coupon if delayed by 48+ hours"
}, {
  text: "10% off use code FIRSTGIFT"
}, {
  text: "Contact us for bulk order discounts"
}];
const HeaderComponent: React.FC<HeaderProps> = ({
  showUser: showUserProp,
  showCart: showCartProp,
  showSearch: showSearchProp,
  isAppLaunched: isAppLaunchedProp,
  navData = DEFAULT_ARR,
  showNavLinks: showNavLinksProp
}) => {
  const {
    showNavLinks: contextShowNavLinks,
    showUser: contextShowUser,
    showCart: contextShowCart,
    showSearch: contextShowSearch,
    isAppLaunched: contextIsAppLaunched
  } = useHeader();

  // Prefer props if provided, otherwise use context
  const showNavLinks = showNavLinksProp !== undefined ? showNavLinksProp : contextShowNavLinks;
  const showUserFromContext = showUserProp !== undefined ? showUserProp : contextShowUser;
  const showCart = showCartProp !== undefined ? showCartProp : contextShowCart;
  const showSearchVisible = showSearchProp !== undefined ? showSearchProp : contextShowSearch;
  const isAppLaunched = isAppLaunchedProp !== undefined ? isAppLaunchedProp : contextIsAppLaunched;
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const [showLoginState, setShowLoginState] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [mounted, setMounted] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const {
    isAuthenticated,
    isAuthLoading,
    logout
  } = useUser();
  const {
    resetCheckout
  } = useCheckout();
  const showUser = showUserFromContext;
  const {
    data: navigation = navData,
    isLoading: isNavigationLoading
  } = useQuery({
    queryKey: ["navigation"],
    queryFn: () => getNavigationLinks(),
    staleTime: Infinity,
    initialData: navData?.length > 0 ? navData : undefined,
    enabled: showNavLinks
  });
  useEffect(() => {
    if (isAuthenticated) {
      setShowLoginState(false);
    }
  }, [isAuthenticated]);
  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await logout();
      resetCheckout();
      setShowUserDropdown(false);
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setIsLoggingOut(false);
    }
  };
  useEffect(() => {
    setMounted(true);
  }, []);
  const renderAnnouncementGroup = () => <div className="flex items-center gap-12 shrink-0 pr-12">
      {ANNOUNCEMENT_ITEMS.map((item, idx) => <span key={idx} className="text-sm font-label font-bold uppercase tracking-widest flex items-center gap-2 text-v2-primary">
          {item.text}{" "}
          {item.highlight && <span className="text-xs font-normal opacity-90">
              {item.highlight}
            </span>}{" "}
          <span className="text-v2-primary-fixed opacity-30 text-xs">•</span>
        </span>)}
    </div>;
  return <>
      <motion.div className="relative top-0 z-[40] bg-white shadow-sm" variants={textVariants} viewport={{
      once: true
    }}>
        <div className="flex-col justify-center w-full left-0">
          {/* Top Marquee Scroller */}
          <div className="w-full py-3 overflow-hidden border-b border-v2-outline-variant/20 relative z-[60] bg-v2-surface-container-low">
            <div className="custom-announcement-marquee whitespace-nowrap flex items-center w-max">
              {renderAnnouncementGroup()}
              {renderAnnouncementGroup()}
            </div>
          </div>
          {/* Desktop Header */}
          <DesktopHeader isAppLaunched={isAppLaunched} showSearchVisible={showSearchVisible} showCart={showCart} setShowSearch={setShowSearch} setShowLoginState={setShowLoginState} isAuthenticated={isAuthenticated} isAuthLoading={isAuthLoading} showUser={showUser} showUserDropdown={showUserDropdown} setShowUserDropdown={setShowUserDropdown} handleLogout={handleLogout} isLoggingOut={isLoggingOut} userDropdownRef={userDropdownRef} mounted={mounted} />

          {/* Mobile Header */}
          <MobileHeader isAppLaunched={isAppLaunched} showSearch={showSearch} showSearchVisible={showSearchVisible} showCart={showCart} setShowSearch={setShowSearch} setShowLoginState={setShowLoginState} isAuthenticated={isAuthenticated} isAuthLoading={isAuthLoading} showUser={showUser} showUserDropdown={showUserDropdown} setShowUserDropdown={setShowUserDropdown} handleLogout={handleLogout} isLoggingOut={isLoggingOut} userDropdownRef={userDropdownRef} setSidebarOpen={setSidebarOpen} showNavLinks={showNavLinks} mounted={mounted} />

          {/* NavigationMenu - server-rendered for instant page-load, using hydration-safe navData fallback */}
          {isAppLaunched && showNavLinks && <NavigationMenu navigation={navigation?.length > 0 ? navigation : navData} />}
        </div>

        {/* Mobile Sidebar */}
        <AnimatePresence>
          {mounted && sidebarOpen && showNavLinks && <MobileSidebar setSidebarOpen={setSidebarOpen} navigation={navigation?.length > 0 ? navigation : navData} isNavigationLoading={isNavigationLoading} setShowLoginState={setShowLoginState} />}
        </AnimatePresence>
      </motion.div>

      {/* Login Popup */}
      <AnimatePresence>
        {isAppLaunched && showLoginState && <LoginPopup setShowLogin={setShowLoginState} />}
      </AnimatePresence>
    </>;
};
const Header: React.FC<HeaderProps> = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <HeaderComponent {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export default Header;